import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileUp, PackageCheck } from "lucide-react";
import { Bouton, LienBouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import {
  dateCourte,
  dateSemaine,
  statutCommandeFournisseur,
  tonStatutCommandeFournisseur,
} from "@/lib/libelles";
import { api } from "@/lib/api";
import { useSession } from "@/session";

/**
 * « Réceptions » (LOT 2, ticket #22) : les commandes fournisseurs parties qui
 * attendent encore quelque chose, dans le périmètre de l'appelant — le serveur
 * l'applique. La logistique en choisit une et constate ce qui est arrivé ; un
 * BL commun à plusieurs commandes du même fournisseur se constate d'un geste,
 * depuis l'écran de réception. Ticket #24 : ou elle dépose le BL, que LABGEST
 * lit et rapproche ; les BL déposés attendent leur validation ici.
 */
export function PageReceptions() {
  const session = useSession();
  // Valider un BL, c'est constater sa réception (ADR-0049) : le serveur le
  // tranche, l'écran n'offre pas un geste qui serait refusé.
  const valideLesBl =
    session.peut("VALIDER_IMPORT_OCR") && session.peut("CONSTATER_RECEPTION");
  const { donnees, erreur, enCours, recharger } = useChargement(
    async (signal) => {
      const [attendues, imports] = await Promise.all([
        api.commandesAReceptionner(signal),
        valideLesBl ? api.importsAValider(signal) : Promise.resolve([]),
      ]);
      return {
        attendues,
        bls: imports.filter((i) => i.type_attendu === "BL_FOURNISSEUR" && i.statut === "A_VALIDER"),
      };
    },
    [valideLesBl],
  );

  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees || enCours) return <Chargement message="Chargement des réceptions attendues…" />;
  const { attendues, bls } = donnees;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-encre">Réceptions</h1>
        <p className="mt-1 text-sm text-encre-douce">
          Les commandes fournisseurs qui attendent encore une livraison à l'atelier. Constatez
          ce qui est arrivé, même au-delà du commandé, et ce qui n'est pas conforme.
        </p>
      </div>
      {session.peut("IMPORTER_DOCUMENT_OCR") && valideLesBl ? <DeposerUnBl /> : null}
      {bls.length > 0 ? (
        <Carte>
          <EnTeteCarte>
            <h2 className="text-sm font-semibold text-encre">BL à valider</h2>
          </EnTeteCarte>
          <ul className="divide-y divide-filet">
            {bls.map((bl) => (
              <li
                key={bl.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <span className="text-encre">BL déposé le {dateCourte(bl.importe_le)}</span>
                <LienBouton taille="compact" to={`/receptions/bl/${bl.id}`}>
                  Contrôler et valider
                </LienBouton>
              </li>
            ))}
          </ul>
        </Carte>
      ) : null}
      {attendues.length === 0 ? (
        <Vide titre="Rien à réceptionner" detail="Aucune commande n'attend de livraison." />
      ) : (
        <Carte>
          <ul className="divide-y divide-filet">
            {attendues.map((commande) => (
              <li
                key={commande.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0 flex-1 text-sm">
                  <p className="break-words font-medium text-encre">
                    <Link
                      to={`/commandes-fournisseurs/${commande.id}`}
                      className="underline underline-offset-4"
                    >
                      {commande.fournisseur.nom}
                    </Link>
                    {commande.reference_fournisseur ? ` — ${commande.reference_fournisseur}` : ""}
                  </p>
                  <p className="text-encre-douce">
                    Pour{" "}
                    <span className="font-mono">
                      {commande.commandes_clients
                        .map((servie) => servie.numero_commande_client)
                        .join(", ")}
                    </span>
                    {" · "}
                    {commande.lignes_attendues > 1
                      ? `${commande.lignes_attendues} lignes attendues`
                      : commande.lignes_attendues === 1
                        ? "1 ligne attendue"
                        : "aucune de vos lignes n'attend"}
                    {" · "}
                    {commande.date_prevue
                      ? `prévue le ${dateSemaine(commande.date_prevue)}`
                      : "date prévue à saisir"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Etiquette ton={tonStatutCommandeFournisseur(commande.statut)}>
                    {statutCommandeFournisseur(commande.statut)}
                  </Etiquette>
                  <LienBouton
                    taille="compact"
                    to={`/receptions/nouvelle?commandes=${commande.id}`}
                    aria-label={`Constater une réception chez ${commande.fournisseur.nom}`}
                  >
                    <PackageCheck className="h-4 w-4" aria-hidden />
                    Constater
                  </LienBouton>
                </div>
              </li>
            ))}
          </ul>
        </Carte>
      )}
    </div>
  );
}

/** Ticket #24 : déposer un BL ; LABGEST le lit et propose sa réception. */
function DeposerUnBl() {
  const naviguer = useNavigate();
  const [fichier, setFichier] = useState<File | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  return (
    <Carte>
      <EnTeteCarte>
        <h2 className="text-sm font-semibold text-encre">Déposer un BL fournisseur</h2>
      </EnTeteCarte>
      <CorpsCarte>
        <form
          aria-label="Déposer un BL fournisseur"
          className="flex flex-col gap-3"
          onSubmit={async (evenement) => {
            evenement.preventDefault();
            if (!fichier) return;
            setRefus(null);
            setEnvoi(true);
            try {
              const depot = await api.deposerBl(fichier);
              naviguer(`/receptions/bl/${depot.id}`);
            } catch (cause) {
              setRefus(messageDe(cause));
              setEnvoi(false);
            }
          }}
        >
          <Champ
            libelle="Fichier du BL"
            type="file"
            accept="application/pdf,image/*"
            className="py-2"
            aide={
              "LABGEST le lit, reconnaît la commande et propose ce qui est arrivé ;" +
              " vous contrôlez avant de valider."
            }
            onChange={(evenement) => setFichier(evenement.target.files?.[0] ?? null)}
          />
          {refus ? <Erreur message={refus} /> : null}
          <div>
            <Bouton type="submit" variante="principal" disabled={!fichier || envoi}>
              <FileUp className="h-4 w-4" aria-hidden />
              Lire le BL
            </Bouton>
          </div>
        </form>
      </CorpsCarte>
    </Carte>
  );
}
